import { ulid } from 'ulid';
import type { VoucherViewModel } from '@/graphql/generated';

type VoucherCloneInput = Pick<VoucherViewModel,
  | 'name'
  | 'code'
  | 'isAdHoc'
  | 'isCustomerSpecific'
  | 'rateBindingRequired'
  | 'discountValue'
  | 'maxDiscountAmount'
  | 'freeTimeMinutes'
  | 'usageLimit'
  | 'validUntilDate'
  | 'isActive'
  | 'campaignId'             // Foreign key - preserved (optional)
  | 'customerId'             // Foreign key - preserved (optional, for customer-specific vouchers)
  | 'facilityId'             // Foreign key - preserved (optional)
  | 'rateId'               // Foreign key - preserved (optional, for rate replacement vouchers)
  | 'voucherDiscountTypeEnum' // Enum - preserved
>;

/**
 * Clone a Voucher entity
 *
 * - voucherId: NEW (regenerated)
 * - code: NEW (must generate unique code - appended with timestamp)
 * - campaignId: PRESERVED (foreign key - optional, voucher belongs to same campaign)
 * - customerId: PRESERVED (foreign key - optional, for customer-specific vouchers)
 * - facilityId: PRESERVED (foreign key - optional, voucher is for same facility)
 * - rateId: PRESERVED (foreign key - optional, for rate replacement vouchers)
 * - voucherDiscountTypeEnum: PRESERVED (enum value)
 * - freeTimeMinutes: PRESERVED (for free time discount type)
 * - currentUsageCount: RESET to 0 (new voucher has no uses)
 * - dateUsed: RESET to null (new voucher hasn't been used)
 * - All other fields: COPIED as-is
 */
export function cloneVoucher(source: VoucherCloneInput): VoucherCloneInput & {
  voucherId: string;
  currentUsageCount: number;
  dateUsed: null;
} {
  // Generate a unique code by appending timestamp
  const timestamp = Date.now().toString(36).toUpperCase();
  const newCode = `${source.code}-${timestamp}`;

  return {
    // New entity ID
    voucherId: ulid(),

    // New unique code (codes must be unique)
    code: newCode,

    // Transform name to indicate copy
    name: source.name ? `${source.name} (Copy)` : source.name,

    // Preserve foreign keys
    campaignId: source.campaignId,
    customerId: source.customerId,
    facilityId: source.facilityId,
    rateId: source.rateId,

    // Preserve enum value
    voucherDiscountTypeEnum: source.voucherDiscountTypeEnum,

    // Reset usage tracking for new voucher
    currentUsageCount: 0,
    dateUsed: null,

    // Copy voucher configuration
    isAdHoc: source.isAdHoc,
    isCustomerSpecific: source.isCustomerSpecific,
    rateBindingRequired: source.rateBindingRequired,
    discountValue: source.discountValue,
    maxDiscountAmount: source.maxDiscountAmount,
    freeTimeMinutes: source.freeTimeMinutes,
    usageLimit: source.usageLimit,
    validUntilDate: source.validUntilDate,
    isActive: source.isActive,
  };
}

export type ClonedVoucher = ReturnType<typeof cloneVoucher>;
